import React, {ChangeEvent, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Input,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {getAmountValue, sortTransactions, type SortDirection} from './sortUtils';
import SkeletonTable from './loaders/SkeletonTable';
import './TransactionTable.css';
import styles from './ExpenseBreakout.module.css';

type DeductionType = 'deductible' | 'notDeductible' | 'partiallyDeductible' | 'ignore';
type BreakoutRow = Record < string,
unknown > & {
    _rowId: number;
    Expense: string;
    DeductionType: DeductionType;
    DeductionPercent: number;
};

type NewExpenseEntry = {
    Date: string;
    Description: string;
    Amount: string;
    Category: string;
    Expense: string;
    DeductionType: DeductionType;
    DeductionPercent: string;
};

type AmountBreakout = {
    transactions: number;
    initial: number;
    deductible: number;
    notDeductible: number;
    ignored: number;
    partial: boolean;
};

type CategoryBreakout = AmountBreakout & {
    descriptions: Record<string, AmountBreakout>;
};

type ExpenseBreakoutTotals = AmountBreakout & {
    categories: Record<string, CategoryBreakout>;
};

const expenseCategories = [
    'Accounting/Tax Prep',
    'Advertising',
    'Bank Charges',
    'Business Gifts',
    'Commissions',
    'Credit/Collection',
    'Computer Services/Supplies',
    'Delivery/Freight',
    'Discounts',
    'Dues/Subscriptions',
    'Equipment Rental',
    'Insurance (other than health and medical)',
    'Interest Expense',
    'Janitorial',
    'Laundry',
    'Legal/Professional Fees',
    'Office Expenses',
    'Office Parking',
    'Printing/Copying',
    'Postage',
    'Repairs/Maintenance',
    'Security',
    'Supplies',
    'Telephone',
    'Tools',
    'Training/Education',
    'Business Travel',
    'Uniforms',
    'Utilities',
    'Taxes and Licenses',
    'Local Property Taxes',
    'Other Taxes',
    'State Income Tax',
    'Licenses, Permits, & Fees',
    'Rent Expense',
    'Office Equipment Rent',
    'Renting Business Vehicles',
    'Rental of office space',
    'Meals and Entertainment',
    'Company Party',
    'Entertainment not for Employees',
    'All other Meal and Entertainment',
    'Auto and Truck Expense',
    'Charitable Contributions',
    'Investment Expense',
    'All other Expenses',
    'Other – Government Fee',
    'Merchandise & Supplies-Internet Purchase',
    'Tolls',
    'Insurance',
    'Health Care Services',
    'Business Services',
    'Fees & Adjustments',
    'Merchandise & Supplies',
    'Transportation',
    'Communications-Cable & Internet Comm',
    'LLC Membership'
];
const allExpensesFilter = '__all_expenses__';

const deductionLabels : Record < DeductionType,
    string > = {
        deductible: 'Deductible',
        notDeductible: 'Not deductible',
        partiallyDeductible: 'Partially deductible',
        ignore: 'Ignore'
    };

type ExpenseGuess = {
    Expense: string;
    DeductionType: DeductionType;
    DeductionPercent: number;
};

const searchableRowText = (row : Record < string, unknown >) : string => Object
    .entries(row)
    .filter(([key]) => !['_rowId', 'Expense', 'DeductionType', 'DeductionPercent'].includes(key))
    .map(([, value]) => String(value ?? ''))
    .join(' ')
    .toLowerCase();

const isAttUversePayment = (row : Record < string, unknown >) : boolean => {
    const description = String(row.Description ?? '').toLowerCase();
    return /at&t\s+u-?verse\s+payment/i.test(description);
};

const isPreferredSourceColumn = (column : string) : boolean => {
    const normalizedColumn = column.toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalizedColumn === 'date' || normalizedColumn.endsWith('date') || normalizedColumn === 'amount' || normalizedColumn.endsWith('amount') || normalizedColumn === 'debitcredit' || normalizedColumn.includes('description') || normalizedColumn.includes('detail') || normalizedColumn.includes('memo') || normalizedColumn.includes('merchant') || normalizedColumn.includes('payee') || normalizedColumn === 'category' || normalizedColumn.endsWith('category');
};

const guessExpenseDetails = (row : Record < string, unknown >) : ExpenseGuess => {
    const text = searchableRowText(row);
    if (/\bcredit\b/i.test(text)) return {Expense: 'Credit/Collection', DeductionType: 'notDeductible', DeductionPercent: 0};
    if (/\b(perot|arboretum)\b/i.test(text)) return {Expense: 'LLC Membership', DeductionType: 'notDeductible', DeductionPercent: 0};
    if (/\bdell\b|computer|laptop|desktop|monitor|keyboard|printer/i.test(text)) return {Expense: 'Computer Services/Supplies', DeductionType: 'deductible', DeductionPercent: 100};
    if (isAttUversePayment(row) || /internet|wifi|wi-fi|broadband|\b(phone|cellular|mobile|telephone)\b/i.test(text)) return {Expense: 'Telephone', DeductionType: 'deductible', DeductionPercent: 100};
    if (/autopay|auto\s*-?\s*pay/i.test(text)) return {Expense: 'Ignore', DeductionType: 'ignore', DeductionPercent: 0};
    if (/\b(ntta|toll|tolls|turnpike|tollway)\b/i.test(text)) return {Expense: 'Tolls', DeductionType: 'notDeductible', DeductionPercent: 0};
    if (/health\s*care|healthcare|medical|doctor|dental|pharmacy|charit|donation|nonprofit/i.test(text)) return {Expense: 'Health Care Services', DeductionType: 'notDeductible', DeductionPercent: 0};
    if (/meal|restaurant|dining|food delivery|entertainment/i.test(text)) return {Expense: 'Meals and Entertainment', DeductionType: 'partiallyDeductible', DeductionPercent: 50};
    if (/electric|electricity|water|utility|utilities|insurance/i.test(text)) return {Expense: 'Utilities', DeductionType: 'partiallyDeductible', DeductionPercent: 15};
    if (/gas|fuel|auto|vehicle|mechanic|oil change|tire/i.test(text)) return {Expense: 'Auto and Truck Expense', DeductionType: 'notDeductible', DeductionPercent: 0};
    if (/travel|airfare|flight|taxi|rideshare|uber|lyft|hotel|lodging|motel/i.test(text)) return {Expense: 'Business Travel', DeductionType: 'deductible', DeductionPercent: 100};
    return {Expense: '', DeductionType: 'deductible', DeductionPercent: 100};
};

const getAmount = (row : BreakoutRow) : number => {
    return getAmountValue(row);
};

const getDeductibleAmount = (row : BreakoutRow) : number => {
    const amount = getAmount(row);
    if (row.DeductionType === 'notDeductible' || row.DeductionType === 'ignore') 
        return 0;
    if (row.DeductionType === 'partiallyDeductible') 
        return amount * row.DeductionPercent / 100;
    return amount;
};

const ExpenseBreakout : React.FC = () => {
    const [rows,
        setRows] = useState < BreakoutRow[] > ([]);
    const [fileName,
        setFileName] = useState('');
    const [hiddenColumns,
        setHiddenColumns] = useState < string[] > ([]);
    const [customExpenseCategories,
        setCustomExpenseCategories] = useState < string[] > ([]);
    const [newExpenseCategory,
        setNewExpenseCategory] = useState('');
    const [expenseFilter,
        setExpenseFilter] = useState<string[]>([]);
    const [filterColumn,
        setFilterColumn] = useState('');
    const [filterValue,
        setFilterValue] = useState('');
    const [sortField,
        setSortField] = useState < string > ('');
    const [sortDirection,
        setSortDirection] = useState < SortDirection > ('asc');
    const [showTotals,
        setShowTotals] = useState(false);
    const [isLoading,
        setIsLoading] = useState(false);
    const [loadingMessage,
        setLoadingMessage] = useState('Loading JSON files...');
    const [isUpdatingTable,
        setIsUpdatingTable] = useState(false);
    const [newExpenseEntry,
        setNewExpenseEntry] = useState<NewExpenseEntry>({
        Date: '',
        Description: '',
        Amount: '',
        Category: '',
        Expense: '',
        DeductionType: 'deductible',
        DeductionPercent: '100'
    });

    const sourceColumns = useMemo(() => {
        const columns = new Set < string > ();
        rows.forEach((row) => Object.keys(row).forEach((key) => {
            if (!['_rowId', 'Expense', 'DeductionType', 'DeductionPercent'].includes(key)) 
                columns.add(key);
            }
        ));
        return Array.from(columns);
    }, [rows]);

    const allColumns = [
        ...sourceColumns,
        'Expense',
        'DeductionType',
        'DeductionPercent'
    ];
    const visibleColumns = allColumns.filter((column) => !hiddenColumns.includes(column));

    const expenseOptions = useMemo(() => Array.from(new Set([
        ...expenseCategories,
        ...customExpenseCategories,
        'Groceries',
        'Other Services',
        'Ignore',
        ...rows.map((row) => row.Expense).filter(Boolean)
    ])).sort(), [customExpenseCategories, rows]);

    const categoryRows = useMemo(() => {
        if (!expenseFilter.length || expenseFilter.includes(allExpensesFilter))
            return rows;
        return rows.filter((row) => expenseFilter.some((selectedExpense) => selectedExpense === '__uncategorized__'
            ? !row.Expense
            : row.Expense === selectedExpense));
    }, [expenseFilter, rows]);

    const displayedRows = useMemo(() => {
        let result = sortField
            ? sortTransactions(categoryRows, sortField, sortDirection)
            : [...categoryRows];
        if (filterValue && filterColumn) {
            const search = filterValue.toLowerCase();
            result = result.filter((row) => String(row[filterColumn] ?? '').toLowerCase().includes(search));
        }
        return result;
    }, [categoryRows, filterColumn, filterValue, sortDirection, sortField]);

    const totals = useMemo(() => {
        const deductible = categoryRows.reduce((sum, row) => sum + (row.DeductionType === 'deductible'
            ? getAmount(row)
            : 0), 0);
        const notDeductible = categoryRows.reduce((sum, row) => sum + (row.DeductionType === 'notDeductible'
            ? getAmount(row)
            : 0), 0);
        const ignored = categoryRows.reduce((sum, row) => sum + (row.DeductionType === 'ignore'
            ? getAmount(row)
            : 0), 0);
        const partialGross = categoryRows.reduce((sum, row) => sum + (row.DeductionType === 'partiallyDeductible'
            ? getAmount(row)
            : 0), 0);
        const partialDeductible = categoryRows.reduce((sum, row) => sum + (row.DeductionType === 'partiallyDeductible'
            ? getDeductibleAmount(row)
            : 0), 0);
        const partialBreakouts = categoryRows.filter((row) => row.DeductionType === 'partiallyDeductible').reduce < Record < number, {gross: number;
                deductible: number} >> ((result, row) => {
                const percentage = row.DeductionPercent;
                const current = result[percentage] ?? {
                    gross: 0,
                    deductible: 0
                };
                result[percentage] = {
                    gross: current.gross + getAmount(row),
                    deductible: current.deductible + getDeductibleAmount(row)
                };
                return result;
            }, {});
        const expenseBreakouts = categoryRows.reduce < Record < string, ExpenseBreakoutTotals >> ((result, row) => {
                const expense = row.Expense || 'Uncategorized';
                const category = String(row.Category ?? '').trim() || 'Uncategorized';
                const description = String(row.Description ?? '').trim() || 'No description';
                const initial = getAmount(row);
                const deductible = getDeductibleAmount(row);
                const current = result[expense] ?? {
                    initial: 0,
                    deductible: 0,
                    notDeductible: 0,
                    ignored: 0,
                    partial: false,
                    transactions: 0,
                    categories: {}
                };
                const currentCategory = current.categories[category] ?? {
                    transactions: 0,
                    initial: 0,
                    deductible: 0,
                    notDeductible: 0,
                    ignored: 0,
                    partial: false,
                    descriptions: {}
                };
                const currentDescription = currentCategory.descriptions[description] ?? {
                    transactions: 0,
                    initial: 0,
                    deductible: 0,
                    notDeductible: 0,
                    ignored: 0,
                    partial: false
                };
                const descriptionBreakout = {
                    transactions: currentDescription.transactions + 1,
                    initial: currentDescription.initial + initial,
                    deductible: currentDescription.deductible + deductible,
                    notDeductible: currentDescription.notDeductible + (row.DeductionType === 'notDeductible'
                        ? initial
                        : row.DeductionType === 'partiallyDeductible'
                            ? initial - deductible
                            : 0),
                    ignored: currentDescription.ignored + (row.DeductionType === 'ignore'
                        ? initial
                        : 0),
                    partial: currentDescription.partial || row.DeductionType === 'partiallyDeductible'
                };
                result[expense] = {
                    transactions: current.transactions + 1,
                    initial: current.initial + initial,
                    deductible: current.deductible + deductible,
                    notDeductible: current.notDeductible + (row.DeductionType === 'notDeductible'
                        ? initial
                        : row.DeductionType === 'partiallyDeductible'
                            ? initial - deductible
                            : 0),
                    ignored: current.ignored + (row.DeductionType === 'ignore'
                        ? initial
                        : 0),
                    partial: current.partial || row.DeductionType === 'partiallyDeductible',
                    categories: {
                        ...current.categories,
                        [category]: {
                            transactions: currentCategory.transactions + 1,
                            initial: currentCategory.initial + initial,
                            deductible: currentCategory.deductible + deductible,
                            notDeductible: currentCategory.notDeductible + (row.DeductionType === 'notDeductible'
                                ? initial
                                : row.DeductionType === 'partiallyDeductible'
                                    ? initial - deductible
                                    : 0),
                            ignored: currentCategory.ignored + (row.DeductionType === 'ignore'
                                ? initial
                                : 0),
                            partial: currentCategory.partial || row.DeductionType === 'partiallyDeductible',
                            descriptions: {
                                ...currentCategory.descriptions,
                                [description]: descriptionBreakout
                            }
                        }
                    }
                };
                return result;
            }, {});
        return {
            deductible,
            notDeductible,
            ignored,
            partialGross,
            partialDeductible,
            partialNotDeductible: partialGross - partialDeductible,
            totalDeductible: deductible + partialDeductible,
            totalNotDeductible: notDeductible + partialGross - partialDeductible,
            partialBreakouts,
            expenseBreakouts
        };
    }, [categoryRows]);

    const handleSaveTotals = () => {
        const savedData = JSON.stringify({
            version: 1,
            rows,
            expenseFilter,
            customExpenseCategories
        }, null, 2);
        const categoryName = expenseFilter.length === 1 && expenseFilter[0] !== allExpensesFilter
            ? expenseFilter[0] === '__uncategorized__' ? 'Uncategorized' : expenseFilter[0]
            : 'All-expenses';
        const safeCategoryName = categoryName.replace(/[<>:"/\\|?*]/g, '-');
        const download = document.createElement('a');
        download.href = URL.createObjectURL(new Blob([savedData], {type: 'application/json'}));
        download.download = `expense-breakout-totals-${safeCategoryName}.json`;
        download.click();
        URL.revokeObjectURL(download.href);
    };

    const handleAddExpenseCategory = () => {
        const category = newExpenseCategory.trim();
        if (!category) 
            return;
        setCustomExpenseCategories((current) => current.includes(category)
            ? current
            : [
                ...current,
                category
            ]);
        setNewExpenseCategory('');
    };

    const handleAddExpenseEntry = () => {
        const nextRow: BreakoutRow = {
            Date: newExpenseEntry.Date,
            Description: newExpenseEntry.Description,
            Amount: newExpenseEntry.Amount,
            Category: newExpenseEntry.Category,
            _rowId: rows.reduce((highestId, row) => Math.max(highestId, row._rowId), -1) + 1,
            Expense: newExpenseEntry.Expense,
            DeductionType: newExpenseEntry.DeductionType,
            DeductionPercent: Math.min(100, Math.max(0, Number(newExpenseEntry.DeductionPercent) || 0))
        };
        setRows((current) => [...current, nextRow]);
        setNewExpenseEntry({
            Date: '',
            Description: '',
            Amount: '',
            Category: '',
            Expense: '',
            DeductionType: 'deductible',
            DeductionPercent: '100'
        });
    };

    const handleFileChange = (event : ChangeEvent < HTMLInputElement >) => {
        const files = Array.from(event.target.files ?? []);
        if (!files.length)
            return;
        setIsLoading(true);
        setLoadingMessage(`Reading ${files.length} JSON file${files.length === 1 ? '' : 's'}...`);
        Promise.all(files.map((file) => file.text().then((text) => ({file, parsed: JSON.parse(text)}))))
            .then((parsedFiles) => {
                setLoadingMessage('Classifying expenses and preparing the table...');
                const importedRows = parsedFiles.flatMap(({parsed}) => {
                    if (!Array.isArray(parsed))
                        throw new Error('Each JSON file must contain an array.');
                    return parsed;
                });
                setRows(importedRows.map((row, index) => ({
                    ...row,
                    _rowId: index,
                    ...guessExpenseDetails(row)
                })));
                setFileName(files.map((file) => file.name).join(', '));
                const importedColumns = Array.from(new Set(importedRows.flatMap((row) => Object.keys(row))));
                setHiddenColumns(importedColumns.filter((column) => column !== 'Expense' && !isPreferredSourceColumn(column)));
                setExpenseFilter([]);
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                window.alert(`Could not load JSON: ${error.message}`);
            });
        event.target.value = '';
    };

    const handleSavedTotalsChange = (event : ChangeEvent < HTMLInputElement >) => {
        const files = Array.from(event.target.files ?? []);
        if (!files.length)
            return;
        Promise.all(files.map((file) => file.text().then((text) => ({
            file,
            savedData: JSON.parse(text)
        })))).then((savedFiles) => {
                if (savedFiles.some(({savedData}) => !Array.isArray(savedData.rows)))
                    throw new Error('A saved totals file does not contain valid rows.');

                const loadedRows: BreakoutRow[] = savedFiles.flatMap(({savedData}) => savedData.rows).map((row: BreakoutRow, index): BreakoutRow => {
                    const rowWithId = {...row, _rowId: index};
                    return /\bcredit\b/i.test(searchableRowText(rowWithId))
                        ? {
                            ...rowWithId,
                            Expense: 'Credit/Collection',
                            DeductionType: 'notDeductible' as DeductionType,
                            DeductionPercent: 0
                        }
                        : /\b(perot|arboretum)\b/i.test(searchableRowText(rowWithId))
                            ? {
                                ...rowWithId,
                                Expense: 'LLC Membership',
                                DeductionType: 'notDeductible' as DeductionType,
                                DeductionPercent: 0
                            }
                            : /\bdell\b/i.test(searchableRowText(rowWithId))
                                ? {
                                    ...rowWithId,
                                    Expense: 'Computer Services/Supplies',
                                    DeductionType: 'deductible' as DeductionType,
                                    DeductionPercent: 100
                                }
                                : isAttUversePayment(rowWithId)
                                    ? {
                                        ...rowWithId,
                                        Expense: 'Communications-Cable & Internet Comm'
                                    }
                                    : rowWithId;
                });
                const loadedCustomCategories = savedFiles.flatMap(({savedData}) => Array.isArray(savedData.customExpenseCategories)
                    ? savedData.customExpenseCategories.filter((category : unknown): category is string => typeof category === 'string')
                    : []);
                setRows(loadedRows);
                setExpenseFilter([]);
                setCustomExpenseCategories(Array.from(new Set(loadedCustomCategories)));
                setFileName(files.map((file) => file.name).join(', '));
                setShowTotals(true);
            })
            .catch((error) => window.alert(`Could not load saved totals: ${error.message}`));
        event.target.value = '';
    };

    const updateRow = (rowId : number, changes : Partial < BreakoutRow >) => {
        setRows((current) => current.map((row) => row._rowId === rowId
            ? {
                ...row,
                ...changes
            }
            : row));
    };

    const updateTableAfterIndicatorPaint = (update : () => void) => {
        setIsUpdatingTable(true);
        window.setTimeout(() => {
            update();
            window.setTimeout(() => setIsUpdatingTable(false), 150);
        }, 30);
    };

    const handleHeaderClick = (column : string) => {
        updateTableAfterIndicatorPaint(() => {
            if (sortField === column) {
                setSortDirection((current) => current === 'asc'
                    ? 'desc'
                    : 'asc');
            } else {
                setSortField(column);
                setSortDirection('asc');
            }
        });
    };

    const formatMoney = (amount : number) => `$${amount.toFixed(2)}`;
    const totalColors = {
        deductible: '#218739',
        notDeductible: '#673c1e',
        partial: '#c2185b'
    };

    return (
        <Box className="MyTable" p={3}>
            <Typography variant="h4" align="center" gutterBottom>Expense Breakout</Typography>
            <Paper
                elevation={0}
                sx={{
                mb: 2,
                p: 2
            }}>
                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <Button variant="outlined" component="label" disabled={isLoading}>
                        Load JSON
                        <input
                            hidden
                            type="file"
                            accept=".json,application/json"
                            multiple
                            onChange={handleFileChange}/>
                    </Button>
                    <Button variant="outlined" component="label" disabled={isLoading}>
                        Load saved totals
                        <input
                            hidden
                            type="file"
                            accept=".json,application/json"
                            multiple
                            onChange={handleSavedTotalsChange}/>
                    </Button>
                    <Typography variant="body2">{fileName || 'No JSON loaded'}</Typography>
                    <TextField
                        select
                        size="small"
                        label="Expense Category"
                        value={expenseFilter}
                        onChange={(event) => {
                        const selected = (typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : event.target.value) as string[];
                        setExpenseFilter(selected.includes(allExpensesFilter) ? [] : selected);
                    }}
                        SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                            const selectedValues = selected as string[];
                            return selectedValues.length ? selectedValues.map((value) => value === '__uncategorized__' ? 'Uncategorized' : value).join(', ') : 'All expenses';
                        }
                    }}
                        sx={{
                        minWidth: 210
                    }}>
                        <MenuItem value={allExpensesFilter}>All expenses</MenuItem>
                        <MenuItem value="__uncategorized__">Uncategorized</MenuItem>
                        {expenseOptions.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small"
                        label="New expense category"
                        value={newExpenseCategory}
                        onChange={(event) => setNewExpenseCategory(event.target.value)}
                        onKeyDown={(event) => {
                        if (event.key === 'Enter') 
                            handleAddExpenseCategory();
                        }}/>
                    <Button
                        variant="outlined"
                        onClick={handleAddExpenseCategory}
                        disabled={!newExpenseCategory.trim()}>
                        Add category
                    </Button>
                    <TextField
                        select
                        size="small"
                        label="Filter column"
                        value={filterColumn}
                        onChange={(event) => setFilterColumn(event.target.value)}
                        sx={{
                        minWidth: 180
                    }}>
                        <MenuItem value="">All columns</MenuItem>
                        {allColumns.map((column) => <MenuItem key={column} value={column}>{column}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small"
                        label="Filter value"
                        value={filterValue}
                        onChange={(event) => setFilterValue(event.target.value)}/>
                    <Button
                        variant="contained"
                        onClick={() => setShowTotals(true)}
                        disabled={!categoryRows.length}>
                        View totals
                    </Button>
                </Box>
                <Box
                    component="form"
                    onSubmit={(event) => {
                    event.preventDefault();
                    handleAddExpenseEntry();
                }}
                    display="flex"
                    gap={2}
                    mt={2}
                    flexWrap="wrap"
                    alignItems="center">
                    <TextField
                        size="small"
                        label="Date"
                        value={newExpenseEntry.Date}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, Date: event.target.value}))}/>
                    <TextField
                        size="small"
                        label="Description"
                        value={newExpenseEntry.Description}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, Description: event.target.value}))}/>
                    <TextField
                        size="small"
                        label="Amount"
                        value={newExpenseEntry.Amount}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, Amount: event.target.value}))}/>
                    <TextField
                        size="small"
                        label="Category"
                        value={newExpenseEntry.Category}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, Category: event.target.value}))}/>
                    <TextField
                        select
                        size="small"
                        label="Expense"
                        value={newExpenseEntry.Expense}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, Expense: event.target.value}))}
                        sx={{minWidth: 210}}>
                        <MenuItem value="">Uncategorized</MenuItem>
                        {expenseOptions.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Deduction Type"
                        value={newExpenseEntry.DeductionType}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, DeductionType: event.target.value as DeductionType}))}>
                        {Object.entries(deductionLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small"
                        label="Deduction %"
                        value={newExpenseEntry.DeductionPercent}
                        onChange={(event) => setNewExpenseEntry((current) => ({...current, DeductionPercent: event.target.value}))}/>
                    <Button type="submit" variant="contained">Add expense</Button>
                </Box>
            </Paper>
            {!!allColumns.length && (
                <Paper
                    elevation={0}
                    sx={{
                    mb: 2,
                    p: 1
                }}>
                    <Box className={styles.visibleColumnsHeader}>
                        <Typography variant="subtitle1">Visible columns</Typography>
                        {isUpdatingTable && !isLoading && (
                            <Box role="status" aria-live="polite" className={styles.tableUpdateIndicator}>
                                <CircularProgress size={18} thickness={5} aria-label="Updating table"/>
                                <Typography variant="body2">Updating table...</Typography>
                            </Box>
                        )}
                    </Box>
                    <Box display="flex" flexWrap="wrap">
                        {allColumns.map((column) => (
                            <label key={column} className={styles.columnToggle}>
                                <Checkbox
                                    size="small"
                                    checked={!hiddenColumns.includes(column)}
                                    onChange={() => updateTableAfterIndicatorPaint(() => setHiddenColumns((current) => current.includes(column)
                                    ? current.filter((item) => item !== column)
                                    : [
                                        ...current,
                                        column
                                    ]))}/> {column}
                            </label>
                        ))}
                    </Box>
                </Paper>
            )}
            <Box sx={{
                position: 'relative'
            }}>
                {isLoading
                    ? (
                        <Paper
                            elevation={0}
                            sx={{
                            overflow: 'auto'
                        }}>
                            <SkeletonTable rows={10} columns={8}/>
                        </Paper>
                    )
                    : (
                        <Paper
                            elevation={0}
                            sx={{
                            overflow: 'auto'
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {visibleColumns.map((column) => (
                                            <TableCell
                                                key={column}
                                                onClick={() => handleHeaderClick(column)}
                                                className={styles.sortableHeader}>
                                                {column}{sortField === column
                                                    ? (sortDirection === 'asc'
                                                        ? ' ↑'
                                                        : ' ↓')
                                                    : ''}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedRows.map((row) => (
                                        <TableRow
                                            key={row._rowId}
                                            sx={{
                                            backgroundColor: row.DeductionType === 'deductible'
                                                ? '#e8ffe9'
                                                : row.DeductionType === 'partiallyDeductible'
                                                    ? '#e0e0e0'
                                                    : '#ffffff'
                                        }}>
                                            {visibleColumns.map((column) => (
                                                <TableCell key={column}>
                                                    {column === 'Expense'
                                                        ? (
                                                            <FormControl size="small" fullWidth>
                                                                <InputLabel id={`expense-label-${row._rowId}`}>Expense</InputLabel>
                                                                <Select
                                                                    labelId={`expense-label-${row._rowId}`}
                                                                    value={String(row.Expense ?? '')}
                                                                    label="Expense"
                                                                    onChange={(event) => updateRow(row._rowId, {Expense: event.target.value})}>
                                                                    <MenuItem value="">Uncategorized</MenuItem>
                                                                    {expenseOptions.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                                                                </Select>
                                                            </FormControl>
                                                        )
                                                        : column === 'DeductionType'
                                                            ? (
                                                                <Select
                                                                    size="small"
                                                                    value={row.DeductionType}
                                                                    onChange={(event) => updateRow(row._rowId, {DeductionType: event.target.value as DeductionType})}>
                                                                    {Object
                                                                        .entries(deductionLabels)
                                                                        .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                                                                </Select>
                                                            )
                                                            : column === 'DeductionPercent'
                                                                ? (<Input
                                                                    type="number"
                                                                    inputProps={{
                                                                    min: 0,
                                                                    max: 100,
                                                                    step: 1
                                                                }}
                                                                    value={row.DeductionType === 'partiallyDeductible'
                                                                    ? row.DeductionPercent
                                                                    : ''}
                                                                    disabled={row.DeductionType !== 'partiallyDeductible'}
                                                                    onChange={(event) => updateRow(row._rowId, {
                                                                    DeductionPercent: Math.min(100, Math.max(0, Number(event.target.value)))
                                                                })}
                                                                    endAdornment="%"/>)
                                                                : String(row[column] ?? '')}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {!rows.length && <Typography
                                sx={{
                                p: 3
                            }}
                                align="center">Load a JSON array to begin.</Typography>}
                        </Paper>
                    )}
                {isLoading && (
                    <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.72)'
                    }}>
                        <CircularProgress size={52} aria-label="Loading"/>
                        <Typography variant="h6">{loadingMessage}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Large files may take a moment on this computer.
                        </Typography>
                    </Box>
                )}
            </Box>
            <Dialog
                open={showTotals}
                onClose={() => setShowTotals(false)}
                maxWidth="sm"
                fullWidth>
                <DialogTitle>
                    Expense deduction totals{expenseFilter.length > 0 && `: ${expenseFilter.map((filter) => filter === '__uncategorized__'
                        ? 'Uncategorized'
                        : filter).join(', ')}`}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{color: totalColors.deductible, fontWeight: 'bold'}}>Fully deductible: {formatMoney(totals.deductible)}</Typography>
                    <Typography sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>Not deductible: {formatMoney(totals.notDeductible)}</Typography>
                    <Typography>Ignored: {formatMoney(totals.ignored)}</Typography>
                    <Typography sx={{color: totalColors.partial, fontWeight: 'bold'}}>Partially deductible: {formatMoney(totals.partialDeductible)}
                        {" "}of {formatMoney(totals.partialGross)}</Typography>
                    {Object
                        .entries(totals.partialBreakouts)
                        .map(([percentage, breakout]) => (
                            <Typography
                                key={percentage}
                                sx={{
                                ml: 2,
                                color: totalColors.partial,
                                fontWeight: 'bold'
                            }}>
                                {percentage}% partial: {formatMoney(breakout.deductible)}
                                {" "}deductible of {formatMoney(breakout.gross)}
                            </Typography>
                        ))}
                    <Typography
                        sx={{
                        mt: 2,
                        fontWeight: 'bold'
                    }}>By Expense category</Typography>
                    <Typography
                        sx={{
                        fontWeight: 'bold'
                    }}>Total transactions: {categoryRows.length}</Typography>
                    {Object
                        .entries(totals.expenseBreakouts)
                        .map(([expense, breakout]) => (
                            <Box
                                key={expense}
                                sx={{
                                ml: 2,
                                mb: 1
                            }}>
                                <Typography
                                    sx={{
                                    fontWeight: 'bold'
                                }}>{expense}</Typography>
                                {breakout.partial
                                    ? (
                                        <Typography variant="body2">
                                            Initial: <Box component="span" sx={{color: totalColors.partial, fontWeight: 'bold'}}>{formatMoney(breakout.initial)}</Box>
                                            | Final deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(breakout.deductible)}</Box>
                                            | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(breakout.notDeductible)}</Box>
                                        </Typography>
                                    )
                                    : (
                                        <Typography variant="body2">
                                            Total: {formatMoney(breakout.initial)}
                                            | Deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(breakout.deductible)}</Box>
                                            | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(breakout.notDeductible)}</Box>
                                            | Ignored: {formatMoney(breakout.ignored)}
                                        </Typography>
                                    )}
                                {expenseFilter.length > 0 && (
                                    <Box
                                        sx={{
                                        ml: 2,
                                        mt: 1
                                    }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                            fontWeight: 'bold'
                                        }}>By Description</Typography>
                                        {(Object
                                            .entries(breakout.categories) as [string, CategoryBreakout][])
                                            .map(([category, categoryBreakout]) => (
                                                <Box
                                                    key={category}
                                                    sx={{
                                                    mb: 1
                                                }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                        fontWeight: 'bold'
                                                    }}>
                                                        Category: {category}
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                            color: 'red'
                                                        }}> ({categoryBreakout.transactions})</Box>
                                                    </Typography>
                                                    {categoryBreakout.partial
                                                        ? (
                                                            <Typography variant="caption" display="block">
                                                                Subtotal: <Box component="span" sx={{color: totalColors.partial, fontWeight: 'bold'}}>{formatMoney(categoryBreakout.initial)}</Box>
                                                                | Final deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(categoryBreakout.deductible)}</Box>
                                                                | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(categoryBreakout.notDeductible)}</Box>
                                                            </Typography>
                                                        )
                                                        : (
                                                            <Typography variant="caption" display="block">
                                                                Subtotal: {formatMoney(categoryBreakout.initial)}
                                                                | Deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(categoryBreakout.deductible)}</Box>
                                                                | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(categoryBreakout.notDeductible)}</Box>
                                                                | Ignored: {formatMoney(categoryBreakout.ignored)}
                                                            </Typography>
                                                        )}
                                                    <Box sx={{ml: 2, mt: 1}}>
                                                        {(Object
                                                            .entries(categoryBreakout.descriptions) as [string, AmountBreakout][])
                                                            .map(([description, descriptionBreakout]) => (
                                                                <Box key={description} sx={{mb: 1}}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                        fontWeight: 'bold'
                                                                    }}>
                                                                        {description}
                                                                        <Box component="span" sx={{color: 'red'}}> ({descriptionBreakout.transactions})</Box>
                                                                    </Typography>
                                                                    {descriptionBreakout.partial
                                                                        ? (
                                                                            <Typography variant="caption" display="block">
                                                                                Initial: <Box component="span" sx={{color: totalColors.partial, fontWeight: 'bold'}}>{formatMoney(descriptionBreakout.initial)}</Box>
                                                                                | Final deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(descriptionBreakout.deductible)}</Box>
                                                                                | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(descriptionBreakout.notDeductible)}</Box>
                                                                            </Typography>
                                                                        )
                                                                        : (
                                                                            <Typography variant="caption" display="block">
                                                                                Total: {formatMoney(descriptionBreakout.initial)}
                                                                                | Deductible: <Box component="span" sx={{color: totalColors.deductible, fontWeight: 'bold'}}>{formatMoney(descriptionBreakout.deductible)}</Box>
                                                                                | Not deductible: <Box component="span" sx={{color: totalColors.notDeductible, fontWeight: 'bold'}}>{formatMoney(descriptionBreakout.notDeductible)}</Box>
                                                                                | Ignored: {formatMoney(descriptionBreakout.ignored)}
                                                                            </Typography>
                                                                        )}
                                                                </Box>
                                                            ))}
                                                    </Box>
                                                </Box>
                                            ))}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    <Typography
                        sx={{
                        mt: 2,
                        fontWeight: 'bold'
                    }}>Total deductible: <Box component="span" sx={{color: totalColors.deductible}}>{formatMoney(totals.totalDeductible)}</Box></Typography>
                    <Typography
                        sx={{
                        fontWeight: 'bold'
                    }}>Total not deductible: <Box component="span" sx={{color: totalColors.notDeductible}}>{formatMoney(totals.totalNotDeductible)}</Box></Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSaveTotals} disabled={!categoryRows.length}>Save totals</Button>
                    <Button onClick={() => setShowTotals(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpenseBreakout;
